import type { WebRtcMediaQuality } from './types';

export type MediaQualityReport = Pick<
  WebRtcMediaQuality,
  | 'active'
  | 'codec'
  | 'route'
  | 'protocol'
  | 'iceState'
  | 'connectionState'
  | 'packetLossPercent'
  | 'outboundPacketLossPercent'
  | 'jitterMs'
  | 'outboundJitterMs'
  | 'rttMs'
  | 'concealmentPercent'
>;

type PendingReport = { callId: string; report: MediaQualityReport };

type MediaQualityReporterOptions = {
  now?: () => number;
  send: (callId: string, report: MediaQualityReport) => Promise<unknown>;
  minIntervalMs?: number;
  onError?: (error: unknown) => void;
};

function toReport(quality: WebRtcMediaQuality): MediaQualityReport {
  return {
    active: quality.active,
    codec: quality.codec,
    route: quality.route,
    protocol: quality.protocol,
    iceState: quality.iceState,
    connectionState: quality.connectionState,
    packetLossPercent: quality.packetLossPercent,
    outboundPacketLossPercent: quality.outboundPacketLossPercent,
    jitterMs: quality.jitterMs,
    outboundJitterMs: quality.outboundJitterMs,
    rttMs: quality.rttMs,
    concealmentPercent: quality.concealmentPercent,
  };
}

function hasReportableData(report: MediaQualityReport): boolean {
  if (report.iceState === 'failed' || report.connectionState === 'failed') return true;
  return [
    report.packetLossPercent,
    report.outboundPacketLossPercent,
    report.jitterMs,
    report.outboundJitterMs,
    report.rttMs,
    report.concealmentPercent,
  ].some(value => value !== null && value !== undefined && Number.isFinite(value));
}

export class MediaQualityReporter {
  private readonly now: () => number;
  private readonly send: MediaQualityReporterOptions['send'];
  private readonly minIntervalMs: number;
  private readonly onError: (error: unknown) => void;
  private pending: PendingReport | null = null;
  private lastCallId = '';
  private lastSentAt: number | null = null;
  private inFlight: Promise<void> | null = null;

  constructor(options: MediaQualityReporterOptions) {
    this.now = options.now || (() => Date.now());
    this.send = options.send;
    this.minIntervalMs = Math.max(1_000, options.minIntervalMs ?? 10_000);
    this.onError = options.onError || (() => undefined);
  }

  async observe(callId: string | null | undefined, quality: WebRtcMediaQuality): Promise<void> {
    if (!callId) return;
    const report = toReport(quality);
    if (quality.active && !hasReportableData(report)) return;
    if (this.lastCallId && this.lastCallId !== callId) await this.flush();
    if (this.lastCallId !== callId) {
      this.lastCallId = callId;
      this.lastSentAt = null;
    }
    this.pending = { callId, report };
    const due = this.lastSentAt === null
      || this.now() - this.lastSentAt >= this.minIntervalMs
      || !quality.active;
    if (due) await this.drain(true);
  }

  async flush(): Promise<void> {
    await this.drain(true);
  }

  reset(): void {
    this.pending = null;
    this.lastCallId = '';
    this.lastSentAt = null;
  }

  private async drain(force: boolean): Promise<void> {
    if (this.inFlight) {
      await this.inFlight;
      if (!this.pending) return;
    }
    if (!this.pending) return;
    if (!force && this.pending.report.active && this.lastSentAt !== null
      && this.now() - this.lastSentAt < this.minIntervalMs) return;

    const pending = this.pending;
    this.pending = null;
    this.lastSentAt = this.now();
    this.inFlight = (async () => {
      try {
        await this.send(pending.callId, pending.report);
      } catch (error) {
        this.onError(error);
      }
    })();
    await this.inFlight;
    this.inFlight = null;

    const queuedAfterSend = this.pending as PendingReport | null;
    if (queuedAfterSend && !queuedAfterSend.report.active) await this.drain(true);
  }
}
